import React from "react";
import { Dialog, DialogPanel, DialogTitle, Description } from "@headlessui/react";
import PrimaryButton from "./PrimaryButton";
import SecondaryButton from "./SecondaryButton";

/**
 * ConfirmDialog — ds/ confirmation modal built on @headlessui/react <Dialog>.
 *
 * Headless UI provides the focus trap, Escape-to-close, and aria wiring
 * (role="dialog", aria-modal) — this component only supplies Warm Precision
 * styling and the confirm/cancel action pattern.
 *
 * Usage:
 *   <ConfirmDialog
 *     open={showConfirm}
 *     title="Regenerate webhook secret?"
 *     message="The old secret will stop working immediately. Any webhook still using it will fail until you update it."
 *     confirmLabel="Regenerate"
 *     variant="danger"
 *     onConfirm={handleRegenerate}
 *     onCancel={() => setShowConfirm(false)}
 *   />
 */

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** "danger" renders the confirm button in the destructive/red style. */
  variant?: "default" | "danger";
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-sm rounded-2xl bg-white shadow-2xl p-6">
          <DialogTitle className="font-semibold text-[15px] text-zinc-800 leading-snug">
            {title}
          </DialogTitle>
          <Description className="text-sm text-zinc-500 mt-2 leading-relaxed">
            {message}
          </Description>

          <div className="flex items-center justify-end gap-2 mt-6">
            <SecondaryButton type="button" onClick={onCancel} disabled={busy}>
              {cancelLabel}
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className={variant === "danger" ? "!bg-red-600 hover:!opacity-90" : ""}
            >
              {busy ? "Working…" : confirmLabel}
            </PrimaryButton>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
